import React, { useState, useEffect, useRef, useCallback } from 'react';
import { observeImpression } from '../lib/track';
import { useNavigate } from 'react-router-dom';
import { t, getLang } from '../lib/i18n';
import { Heart, Play } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { thumbUrl, prefetchCatalog } from '../lib/api';
import ShareMenu from './ShareMenu';
import UrgencyBadge from './UrgencyBadge';
import { useGeo, formatKm } from '../context/GeoContext';
import { MapPin, CalendarDays, BookOpen, ArrowRight } from 'lucide-react';

const SLIDE_INTERVAL = 1800;
const PLACEHOLDER = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDMwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjMwMCIgaGVpZ2h0PSI0MDAiIGZpbGw9IiMxNDE0MTQiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzgwODA4MCIgZm9udC1zaXplPSIxNiI+Q2F0YWxvZ3VlPC90ZXh0Pjwvc3ZnPg==';

const shortDate = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString(getLang() === 'ar' ? 'ar-AE' : 'fr-FR', { day: 'numeric', month: 'short' }) : '');

const DealCard = ({ deal, isNew = false }) => {
  const { favorites, toggleFavorite } = useAuth();
  const { distanceFor } = useGeo();
  const km = distanceFor(deal.brand_id);
  const daysLeft = deal.date_end ? Math.round((new Date(`${deal.date_end}T00:00:00`) - new Date().setHours(0, 0, 0, 0)) / 86400000) : null;
  const navigate = useNavigate();
  const cardRef = useRef(null);
  useEffect(() => observeImpression(cardRef.current, deal.id), [deal.id]);
  const liked = favorites.includes(deal.id);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const timerRef = useRef(null);
  const slides = deal.images.slice(0, 6);
  const hasSlideshow = slides.length > 1;

  const stopSlideshow = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    setActiveSlide(0);
  }, []);

  useEffect(() => {
    if (isHovering && hasSlideshow) {
      timerRef.current = setInterval(() => setActiveSlide((p) => (p + 1) % slides.length), SLIDE_INTERVAL);
    } else {
      stopSlideshow();
    }
    return stopSlideshow;
  }, [isHovering, hasSlideshow, slides.length, stopSlideshow]);

  const handleLike = (e) => {
    e.stopPropagation();
    toggleFavorite(deal.id);
  };

  const badgeClass = deal.badge === 'Dernière chance' || deal.badge === 'Last chance' ? 'bg-amber-500' : 'bg-[#E50914]';

  return (
    <>
      <div
        ref={cardRef}
        data-testid="deal-card"
        className="lift-card group relative bg-[#141414] rounded-lg overflow-hidden cursor-pointer border border-[#262626]"
        onMouseEnter={() => { setIsHovering(true); prefetchCatalog(deal); }}
        onTouchStart={() => prefetchCatalog(deal)}
        onMouseLeave={() => setIsHovering(false)}
        onClick={() => navigate(`/catalogue/${deal.id}`)}
      >
        <div className="relative aspect-[2/3] overflow-hidden bg-[#1a1a1a]">
          {!imageLoaded && <div className="absolute inset-0 bg-[#262626] animate-pulse" />}

          {slides.map((src, idx) => (
            (idx === 0 || isHovering) && (
            <img
              key={idx}
              src={thumbUrl(src)}
              alt={deal.brand}
              loading="lazy"
              decoding="async"
              className="lift-img absolute inset-0 w-full h-full object-cover"
              style={{ opacity: idx === activeSlide ? 1 : 0 }}
              onLoad={() => { if (idx === 0) setImageLoaded(true); }}
              onError={(e) => { e.target.src = PLACEHOLDER; if (idx === 0) setImageLoaded(true); }}
            />
            )
          ))}

          {isHovering && hasSlideshow && (
            <div className="absolute bottom-0 left-0 right-0 z-20 flex gap-[2px] px-2 pb-2" data-testid="slideshow-progress">
              {slides.map((_, idx) => (
                <div key={idx} className="flex-1 h-[3px] rounded-full overflow-hidden bg-white/20">
                  <div
                    className="h-full rounded-full"
                    style={{
                      backgroundColor: '#E50914',
                      width: idx <= activeSlide ? '100%' : '0%',
                      ...(idx === activeSlide ? { animation: `progressFill ${SLIDE_INTERVAL}ms linear forwards` } : {}),
                    }}
                  />
                </div>
              ))}
            </div>
          )}

          <div className="absolute top-3 left-3 z-10 flex flex-col items-start gap-1.5">
            {deal.badge && (
              <div data-testid="deal-badge" className={`shine ${badgeClass} text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow-lg tracking-wider uppercase`}>
                {t(deal.badge)}
              </div>
            )}
            <UrgencyBadge deal={deal} />
          </div>

          <button data-testid="like-button" onClick={handleLike}
            className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-black/40 backdrop-blur-sm hover:bg-black/60 transition-all active:scale-90">
            <Heart size={17} className={`transition-colors ${liked ? 'fill-[#E50914] text-[#E50914]' : 'text-white/80'}`} />
          </button>
          <div className="absolute top-14 right-3 z-10 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
            <ShareMenu deal={deal} />
          </div>
          {km != null && (
            <span className="absolute bottom-3 right-3 z-10 inline-flex items-center gap-1 text-[11px] font-bold text-white bg-emerald-600/90 backdrop-blur-sm px-2 py-1 rounded-full shadow-lg" data-testid="deal-distance">
              <MapPin size={11} /> {formatKm(km)}
            </span>
          )}
          {isNew && (
            <div className="absolute bottom-3 left-3 z-10 bg-white text-black text-[10px] font-black px-2 py-0.5 rounded-sm uppercase tracking-wider" data-testid="new-badge">{t('Nouveau')}</div>
          )}
          {deal.has_video && (
            <div className="absolute top-3 right-14 z-10 inline-flex items-center gap-1 h-6 bg-[#E50914] text-white text-[10px] font-black px-2 rounded-full uppercase tracking-wider shadow-lg" data-testid="deal-video-badge"><Play size={9} fill="currentColor" /> {t('Vidéo')}</div>
          )}
        </div>

        <div className="p-3.5 pt-3">
          <div className="flex items-center gap-2.5 min-w-0">
            {deal.brand_logo ? (
              <img src={thumbUrl(deal.brand_logo)} alt="" loading="lazy" draggable={false} data-testid="deal-brand-logo"
                className="w-9 h-9 rounded-full object-contain p-1 bg-white border border-white/15 shadow-sm shrink-0" />
            ) : (
              <span className="w-9 h-9 rounded-full bg-[#E50914]/15 text-[#E50914] font-black text-sm flex items-center justify-center shrink-0" data-testid="deal-brand-initial">{deal.brand?.[0]}</span>
            )}
            <div className="min-w-0">
              <h3 className="font-bold text-white text-sm md:text-base leading-tight truncate tracking-tight" data-testid="deal-brand">{deal.brand}</h3>
              <p className="mt-0.5 flex items-center gap-1 text-[11px] text-[#B3B3B3] whitespace-nowrap overflow-hidden" data-testid="deal-dates">
                <CalendarDays size={11} className="shrink-0 opacity-70" />
                <span className="truncate">{shortDate(deal.date_start)} <span className="opacity-60">→</span> {shortDate(deal.date_end)}</span>
                {daysLeft != null && daysLeft > 3 && <span className="shrink-0 text-[#808080] hidden sm:inline">· {daysLeft} {t('jours')}</span>}
              </p>
            </div>
          </div>
          <button data-testid="deal-discover-btn"
            className="mt-3 w-full h-9 rounded-full bg-[#E50914] text-white text-xs font-bold inline-flex items-center justify-center gap-1.5 hover:bg-[#c4070f] transition-all active:scale-[0.98] group-hover:shadow-[0_8px_20px_rgba(229,9,20,0.35)]">
            <BookOpen size={14} /> {t('Lire le catalogue')} <ArrowRight size={14} className="rtl:-scale-x-100 transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5" />
          </button>
        </div>
      </div>
    </>
  );
};

export default DealCard;
